import { deploymentModels, deploymentNodes, deploymentOptions } from '../data';
import type { ModelId, Specification } from '../types';

interface ComponentDetailsProps {
  model: ModelId;
  activeNode: string | null;
}

const Specifications = ({ rows }: { rows: Specification[] }) => (
  <dl className="mt-1.5 mb-3.5 border-t border-secondary">
    {rows.map(({ label, value }) => (
      <div key={`${label}-${value}`} className="flex justify-between gap-3 border-b border-secondary py-[9px] text-[15px]">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="text-right font-semibold">{value}</dd>
      </div>
    ))}
  </dl>
);

const ComponentDetails = ({ model, activeNode }: ComponentDetailsProps) => {
  const current = deploymentModels[model];
  const node = activeNode ? deploymentNodes[activeNode] : null;
  const option = deploymentOptions.find(option => option.id === model)!;
  const provided = Object.keys(current.nodes)
    .filter(id => deploymentNodes[id].specs || deploymentNodes[id].tiers)
    .map(id => ({ label: 'You provide', value: deploymentNodes[id].title }));

  return (
    <aside className="self-start rounded-[18px] border border-border bg-card p-[22px] min-[901px]:sticky min-[901px]:top-24" aria-live="polite" id="deployment-details">
      <div className="text-sm font-semibold text-(--deployment-accent)">{node?.location || `${option.label} deployment`}</div>
      <h2 className="mt-1 mb-2.5 text-2xl leading-[1.15] font-semibold tracking-tight">{node?.title || 'Select a component'}</h2>
      <p className="mb-3.5">{node?.description || current.summary}</p>
      {node ? (
        <>
          {node.specs && <Specifications rows={node.specs} />}
          {node.tiers?.map(tier => (
            <div key={tier.title}>
              <h3 className="mt-4 mb-0.5 text-[15px] font-semibold text-(--deployment-accent)">{tier.title}</h3>
              <Specifications rows={tier.specs} />
            </div>
          ))}
          {node.managed && (
            <div className="rounded-lg bg-secondary px-3 py-2.5 text-[14.5px] text-muted-foreground">
              Hosted, maintained and scaled by Somatiq. Nothing for your IT team to provision.
            </div>
          )}
        </>
      ) : (
        <>
          <p className="mb-3.5">Components with a coloured dot sit on your premises. Click them to see the hardware your IT team needs to provide.</p>
          <Specifications rows={provided} />
        </>
      )}
    </aside>
  );
};

export default ComponentDetails;
